export const COMPANION_APP_PATHS = {
  home: "/app",
  checkIn: "/app/check-in",
  plan: "/app/plan",
  progress: "/app/progress",
  learn: "/app/learn",
  companion: "/app/companion",
};

export const COMPANION_APP_PAGES = [
  {
    key: "home",
    label: "Profile",
    shortLabel: "Home",
    path: COMPANION_APP_PATHS.home,
  },
  {
    key: "checkIn",
    label: "Current state",
    shortLabel: "Check in",
    path: COMPANION_APP_PATHS.checkIn,
  },
  {
    key: "plan",
    label: "Your output",
    shortLabel: "Plan",
    path: COMPANION_APP_PATHS.plan,
  },
  {
    key: "progress",
    label: "Response history",
    shortLabel: "Progress",
    path: COMPANION_APP_PATHS.progress,
  },
  {
    key: "learn",
    label: "Methodology",
    shortLabel: "Learn",
    path: COMPANION_APP_PATHS.learn,
  },
  {
    key: "companion",
    label: "Adaptive Companion",
    shortLabel: "AI",
    path: COMPANION_APP_PATHS.companion,
  },
];

export const COMPANION_KNOWLEDGE_TOPICS = [
  {
    key: "adaptive-loop",
    eyebrow: "Core methodology",
    title: "The Adaptive Loop",
    summary:
      "A state receives an input, adapts, produces behaviour, and returns feedback that shapes the next response.",
    body:
      "Adaptive Physiology treats behaviour as part of a repeating physiological loop. Companion identifies the directly reported state, matches a physical, environmental, respiratory, recreational, or recovery input, then records what happened afterward. That response becomes feedback for the next match.",
    tags: ["all"],
  },
  {
    key: "learned-physiology",
    eyebrow: "How the nervous system adapts",
    title: "Your physiology has already learned how to be you",
    summary:
      "Repeated experiences can become automatic physiological responses before conscious thought has time to intervene.",
    body:
      "Stress, avoidance, coping behaviours, and repeated physiological demands write themselves into how the body responds. Adaptive Physiology treats these patterns as learned adaptations rather than fixed character traits.",
    points: [
      {
        label: "Input",
        body: "A repeated experience—a stressor, coping behaviour, environmental condition, or physiological demand.",
      },
      {
        label: "Adaptation",
        body: "The nervous system encodes a faster and more efficient response to that repeated input.",
      },
      {
        label: "Automation",
        body: "The response begins firing before conscious thought and becomes part of the default state.",
      },
    ],
    tags: ["all", "adaptive-loop"],
  },
  {
    key: "exercise-as-signal",
    eyebrow: "Exercise physiology",
    title: "Exercise is not only fitness. It is a direct input into the nervous system",
    summary:
      "Movement dose, modality, timing, and recovery can change the physiological state that behaviour emerges from.",
    body:
      "A deliberately selected movement stimulus can influence heart-rate variability, cortisol rhythm, vagal tone, neurotransmitter availability, recovery, and the autonomic set point. In the methodology, exercise is used as a physiological signal—not only as a fitness task.",
    cards: [
      {
        title: "HRV",
        label: "Autonomic capacity",
        body: "Adds context about variability and the range of demand the system may be receiving.",
      },
      {
        title: "Vagal tone",
        label: "Parasympathetic access",
        body: "Supports the transition from completed activation toward recovery.",
      },
      {
        title: "BDNF",
        label: "Neuroplasticity",
        body: "Supports the nervous system’s capacity to encode new patterns through repeated experience.",
      },
      {
        title: "Cortisol",
        label: "Stress calibration",
        body: "Exercise and recovery can help recalibrate the stress response rather than only suppressing it.",
      },
    ],
    tags: [
      "all",
      "organize-high-activation",
      "organize-overwhelm",
      "increase-engagement",
      "restore-activation",
    ],
  },
  {
    key: "knowledge-pillars",
    eyebrow: "Scientific foundations",
    title: "Six knowledge pillars support the methodology",
    summary:
      "Adaptive Physiology integrates exercise, nervous-system adaptation, stress, behaviour, recovery, and performance.",
    body:
      "Companion does not treat these disciplines as isolated information. It uses them together to interpret the report, choose the physiological direction, scale the dose, complete recovery, and observe whether behaviour changes.",
    cards: [
      {
        title: "Exercise Physiology",
        label: "Pillar 01",
        body: "How movement dose and modality change the underlying physiological state.",
      },
      {
        title: "Neuroplasticity",
        label: "Pillar 02",
        body: "How repeated physiological and behavioural experience reshapes default responses.",
      },
      {
        title: "Stress Physiology",
        label: "Pillar 03",
        body: "The HPA axis, allostatic load, HRV, vagal tone, load, recovery, and set point.",
      },
      {
        title: "Behavioural Science",
        label: "Pillar 04",
        body: "Environment, reinforcement, repetition, and habit as parts of a physiological loop.",
      },
      {
        title: "Recovery Science",
        label: "Pillar 05",
        body: "Sleep, parasympathetic access, repair, pacing, and adaptation between doses of load.",
      },
      {
        title: "Human Performance",
        label: "Pillar 06",
        body: "Autonomic capacity and the physiology behind sustained, repeatable output under demand.",
      },
    ],
    tags: ["all"],
  },
  {
    key: "integrated-science",
    eyebrow: "Connecting the dots",
    title: "Established science, integrated into a working framework",
    summary:
      "The contribution is integrative: established disciplines are organized into a practical closed loop.",
    body:
      "Adaptive Physiology brings together exercise physiology, autonomic neuroscience, behavioural science, and recovery or rehabilitation practice. Companion applies those disciplines in sequence so the adult can report the state, receive an input, complete recovery, observe behaviour, and use the result as feedback.",
    cards: [
      {
        title: "Exercise physiology",
        label: "Physiological input",
        body: "Movement is one of the most direct practical inputs available to the nervous system.",
      },
      {
        title: "Autonomic neuroscience",
        label: "State and recovery",
        body: "HRV, vagal tone, stress physiology, sympathetic activation, and parasympathetic recovery.",
      },
      {
        title: "Behavioural science",
        label: "Real-world output",
        body: "Environment, reinforcement, repetition, and habit reveal what the physiological state produces.",
      },
      {
        title: "Recovery and rehabilitation",
        label: "Applied context",
        body: "The setting where physiological input, recovery, capacity, and behaviour meet participation.",
      },
    ],
    tags: ["all"],
  },
  {
    key: "adaptive-markers",
    eyebrow: "What the loop observes",
    title: "Six practical markers connect physiology to behaviour",
    summary:
      "The methodology combines direct self-report, optional autonomic context, recovery, participation, and observable behaviour.",
    body:
      "No single marker explains the entire person. Companion uses the adult’s direct state report as the primary selector and can add optional context from heart rate or HRV, sleep and recovery, preparation, participation, and the behavioural response after the intervention.",
    cards: [
      {
        title: "HRV",
        label: "Heart-rate variability",
        body: "A supporting index compared with the person’s own similar-condition readings.",
      },
      {
        title: "Vagal tone",
        label: "Parasympathetic access",
        body: "The capacity to move from completed activation toward recovery.",
      },
      {
        title: "BDNF",
        label: "Neuroplasticity substrate",
        body: "A mechanism associated with the nervous system’s ability to encode new patterns.",
      },
      {
        title: "Cortisol rhythm",
        label: "Stress calibration",
        body: "The normal daily stress-response rhythm that the methodology aims to support rather than blunt.",
      },
      {
        title: "Sleep architecture",
        label: "Recovery and consolidation",
        body: "Sleep and recovery remain separate variables and generate their own support routines.",
      },
      {
        title: "Behavioural signal",
        label: "Real-world output",
        body: "The urge, participation, and observed response show whether another pattern became available.",
      },
    ],
    tags: ["all", "hrv", "sleep", "recovery"],
  },
  {
    key: "physiology-first",
    eyebrow: "Methodology design",
    title: "The methodology is built around physiology first",
    summary:
      "The central target is the physiological state shaping behaviour, not behaviour treated in isolation.",
    body:
      "Companion uses a closed loop: assess, match the physiological output, complete recovery, reinforce participation, and reassess. Progress is reflected in a wider useful response range, repeatable recovery, and different behaviour becoming more available.",
    points: [
      {
        label: "Primary target",
        body: "The physiological state driving the behavioural response.",
      },
      {
        label: "Change mechanism",
        body: "Exercise physiology, behavioural conditioning, environmental adaptation, breathing, recreation, and recovery.",
      },
      {
        label: "Measurement",
        body: "Self-report, optional HRV and heart rate, sleep, engagement, capacity, completion, and response markers.",
      },
      {
        label: "Feedback loop",
        body: "Assess → match → participate → recover → observe → reassess.",
      },
      {
        label: "Definition of progress",
        body: "A broader adaptive range and more useful behaviour becoming available with less effort.",
      },
    ],
    tags: ["all"],
  },
  {
    key: "why-approaches-fall-short",
    eyebrow: "Why physiology matters",
    title: "Four reasons many behaviour-change approaches fall short",
    summary:
      "Approaches become fragile when they separate intention from the physiology determining what is available under pressure.",
    body:
      "Adaptive Physiology does not reject insight, counselling, medication, coping tools, or other care. It adds the physiological state that can make those tools more or less accessible when demand rises.",
    points: [
      {
        label: "The mind–body split",
        body: "Cognition and physiology are treated as separate systems even though the nervous system shapes what can be carried out.",
      },
      {
        label: "Willpower fatigue",
        body: "Strategies depending only on effort are most fragile during dysregulation, exactly when they are needed most.",
      },
      {
        label: "Symptom management instead of state change",
        body: "Symptoms may quiet while the physiological conditions producing the familiar response remain unchanged.",
      },
      {
        label: "Exercise treated as fitness, not as signal",
        body: "A powerful physiological input is reduced to a wellness accessory instead of being deliberately matched to the state.",
      },
    ],
    tags: ["all", "behaviour-output"],
  },
  {
    key: "high-sympathetic",
    eyebrow: "High activation",
    title: "Matching fight-or-flight activation",
    summary:
      "Purposeful physical output can organize sympathetic activation before a deliberate recovery transition.",
    body:
      "Anger, irritability, restlessness, urgency, elevated heart rate, and a wired feeling commonly occur with sympathetic mobilization. In this state, alertness, muscular readiness, adrenaline, and cortisol release are commonly involved. Companion matches that activation to an appropriate output relative to readiness and conditioning, then completes the intervention through cooldown and parasympathetic recovery.",
    tags: ["organize-high-activation", "organize-overwhelm", "high-activation"],
  },
  {
    key: "reduced-activation",
    eyebrow: "Low activation",
    title: "Building activation and engagement",
    summary:
      "Low, withdrawn, numb, or depleted states usually need a receivable activation signal rather than more pressure.",
    body:
      "When the reported state reflects reduced activation or engagement, Companion prioritizes accessible movement, circulation, light, environment, rhythm, and achievable participation. The dose increases engagement without turning the intervention into another draining demand.",
    tags: ["increase-engagement", "restore-activation", "low-activation", "under-recovered"],
  },
  {
    key: "readiness-dose",
    eyebrow: "Decision logic",
    title: "State selects direction; readiness selects dose",
    summary:
      "The nervous-system report determines the physiological direction. Physical readiness determines what can be performed.",
    body:
      "Companion keeps separate variables separate. The directly reported state determines the target. Physical readiness decides whether the person can use unrestricted, light-to-moderate, modified, or non-exercise input. Conditioning scales the relative dose. Hydration and nourishment prepare the intervention rather than redefining the state.",
    tags: ["all"],
  },
  {
    key: "cooldown-recovery",
    eyebrow: "Complete intervention",
    title: "Why recovery is part of the output",
    summary:
      "The intervention is not complete when exertion stops. The transition toward recovery is intentionally programmed.",
    body:
      "Hard or moderate output is followed by a progressive reduction in demand. Walking, gradually reduced cycling, breathing, mobility, or another matched cooldown allows heart rate, breathing, muscular tension, and urgency to move toward recovery. Output and parasympathetic recovery form one complete intervention.",
    tags: [
      "organize-high-activation",
      "organize-overwhelm",
      "maintain-capacity",
      "use-available-capacity",
    ],
  },
  {
    key: "preparation",
    eyebrow: "Before activity",
    title: "Hydration and nourishment prepare the intervention",
    summary:
      "Preparation supports the chosen output without replacing the physiological target.",
    body:
      "Companion adds water, familiar nourishment, or brief controlled breathing when the report indicates they are useful. These steps may change when the activity begins, but they do not silently replace the state-matched output.",
    tags: ["preparation"],
  },
  {
    key: "morning-bedtime",
    eyebrow: "Separate routines",
    title: "Morning and bedtime routines have different jobs",
    summary:
      "Morning preparation influences how the day begins. Bedtime support responds to sleep and settling difficulty.",
    body:
      "Morning preparation can include hydration, light, breathing, mobility, or a short movement signal. Evening and bedtime support can include an easy after-meal walk, lower-stimulation breathing, gentle mobility, stretching, or somatic work. These routines do not overwrite the main state-matched intervention.",
    tags: ["sleep", "recovery"],
  },
  {
    key: "behaviour-output",
    eyebrow: "Behavioural response",
    title: "Behaviour is an output of state",
    summary:
      "The likely behavioural urge helps define the real-world problem the intervention needs to change.",
    body:
      "Reacting, withdrawing, seeking familiar relief, moving, or reaching for connection can emerge from the current state and environment. Companion does not treat the urge as a character judgment. It uses the urge to create another available response before the familiar pattern completes itself.",
    tags: ["all"],
  },
  {
    key: "hrv-context",
    eyebrow: "Optional biometric",
    title: "Heart rate and HRV add context",
    summary:
      "Camera measurements support self-observation but do not replace the directly reported state.",
    body:
      "A camera reading can add pulse and variability context when signal quality is adequate. The useful comparison is with the person’s own readings under similar conditions. Direct state report and physical readiness remain the primary decision inputs.",
    tags: ["hrv"],
  },
  {
    key: "problem-solving",
    eyebrow: "Adaptive Companion",
    title: "Declining the first option begins problem solving",
    summary:
      "The physiological target can remain stable while the delivery method becomes easier to accept.",
    body:
      "Adaptive Companion can move from the strongest state-matched output to familiar moderate output, a selected sport or recreation, a similar game or skill, somatic work, or the smallest useful input. Something useful remains available when the first recommendation does not fit.",
    tags: ["all"],
  },
];

const unique = (values) => [...new Set((values || []).filter(Boolean))];

export function getCompanionPageFromPath(pathname = "/app") {
  const match = COMPANION_APP_PAGES.find((page) => page.path === pathname);
  if (match) return match.key;
  if (pathname.startsWith("/app/check-in")) return "checkIn";
  if (pathname.startsWith("/app/plan")) return "plan";
  if (pathname.startsWith("/app/progress")) return "progress";
  if (pathname.startsWith("/app/learn")) return "learn";
  if (pathname.startsWith("/app/companion")) return "companion";
  return "home";
}

export function getRelevantCompanionTopics({ assessment, plan, biometrics } = {}) {
  const tags = unique([
    "all",
    assessment?.profile?.key,
    plan?.currentState?.target,
    plan?.preparation?.required ? "preparation" : null,
    plan?.routines?.bedtime?.enabled ? "sleep" : null,
    plan?.routines?.bedtime?.enabled ? "recovery" : null,
    biometrics?.hrvMs ? "hrv" : null,
  ]);

  return COMPANION_KNOWLEDGE_TOPICS.map((topic) => ({
    ...topic,
    relevance: topic.tags.reduce(
      (score, tag) => score + (tags.includes(tag) ? (tag === "all" ? 1 : 5) : 0),
      0,
    ),
  })).sort(
    (a, b) => b.relevance - a.relevance || a.title.localeCompare(b.title),
  );
}

export function buildCompanionPriorities({ assessment, plan, summary, entries = [] } = {}) {
  const priorities = [];

  if (!plan) {
    priorities.push({
      key: "check-in",
      page: "checkIn",
      eyebrow: "Start here",
      title: "Report your current state",
      body:
        "Your saved Adaptive Profile is the baseline. A current-state check-in selects today’s physiological direction.",
      level: 100,
    });
  } else {
    priorities.push({
      key: "current-output",
      page: "plan",
      eyebrow: "Most relevant now",
      title: plan.immediate?.title || "Review today’s matched output",
      body:
        plan.immediate?.instruction ||
        "Open the complete intervention, preparation, cooldown, and recovery sequence.",
      level: 100,
    });
  }

  if (plan?.preparation?.required) {
    priorities.push({
      key: "preparation",
      page: "plan",
      eyebrow: "Before the output",
      title: "Complete the preparation first",
      body:
        plan.preparation.steps?.[0] ||
        "Review the hydration, nourishment, and breathing preparation.",
      level: 92,
    });
  }

  if (plan?.routines?.bedtime?.enabled) {
    priorities.push({
      key: "bedtime",
      page: "plan",
      eyebrow: "Later today",
      title: "Your bedtime support is separate",
      body:
        plan.routines.bedtime.eveningWalk ||
        plan.routines.bedtime.instruction ||
        "Review the evening and bedtime routine generated from the sleep report.",
      level: 86,
    });
  }

  if ((summary?.week?.length || 0) > 0) {
    priorities.push({
      key: "progress",
      page: "progress",
      eyebrow: "Response learning",
      title: `${summary?.completed?.length || 0} completed inputs this week`,
      body:
        "Review which interventions were better matched, unchanged, or less matched under similar conditions.",
      level: 72,
    });
  } else {
    priorities.push({
      key: "first-response",
      page: "progress",
      eyebrow: "Build useful evidence",
      title: "Log the response after the first action",
      body:
        "Companion becomes more useful when it can compare the state before and after a completed input.",
      level: 68,
    });
  }

  const latest = entries.find((entry) => entry.completed);
  if (latest) {
    priorities.push({
      key: "latest-response",
      page: "progress",
      eyebrow: "Latest observation",
      title:
        latest.response === "better"
          ? "The last input felt better matched"
          : latest.response === "worse"
            ? "The last input felt less matched"
            : "The last input produced no clear change",
      body:
        "Use the response history as context rather than treating one result as a final verdict.",
      level: 66,
    });
  }

  priorities.push({
    key: "learn",
    page: "learn",
    eyebrow: "Understand the method",
    title: `Learn what applies to ${assessment?.profile?.title || "your profile"}`,
    body:
      "Review the Adaptive Loop, state-matched output, exercise physiology, recovery, sleep, behaviour, and HRV inside the app.",
    level: 48,
  });

  priorities.push({
    key: "ai",
    page: "companion",
    eyebrow: "Problem solving",
    title: "Ask Adaptive Companion",
    body:
      "The AI explains the approved result and helps change intensity, duration, recreation, or somatic delivery when the first option does not fit.",
    level: 46,
  });

  return priorities.sort((a, b) => b.level - a.level);
}
